import { ConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { FileCheck } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useToasts } from './use-toasts.ts';

export type UseConfirmDeleteHook = {
    confirmDelete: (draftId: string) => void;
};

export function useConfirmDelete(): UseConfirmDeleteHook {
    const { t } = useTranslation('sales-draft');
    const sendEvent = useAnalytics();
    const navigate = useNavigate();
    const { showUpdateErrorToast } = useToasts();

    const { mutate } = api.platoUser.command.salesDraft.draft.delete.useMutation({
        onError: showUpdateErrorToast,
        onSuccess: () => {
            void navigate('/drafts', { replace: true });
        },
    });

    const confirmDelete = useCallback(
        (draftId: string): void => {
            void DialogStateManager.show(ConfirmationDialog, {
                focus: 'destroy',
                icon: FileCheck,
                title: t('sales_draft__cancel_confirmation_dialog__title'),
                description: t('sales_draft__cancel_confirmation_dialog__description'),
                accept: {
                    text: t('sales_draft__cancel_confirmation_dialog__accept'),
                    action: () => {
                        sendEvent('transaction_draft_deleted', {
                            draft_id: draftId,
                        });
                        mutate({ draft_order_id: draftId });
                    },
                },
                reject: {
                    text: t('sales_draft__cancel_confirmation_dialog__reject'),
                },
            });
        },
        [mutate, sendEvent, t],
    );

    return {
        confirmDelete,
    };
}
